// ------------------------------------------------- //
//                                                   //
//                                                   //
//    Imports                                        //
//                                                   //
//                                                   //
// ------------------------------------------------- //

import 'devextreme/dist/css/dx.material.blue.light.compact.css';
import config from 'devextreme/core/config';
import '@/ui/theme/global.scss';
import '@/ui/theme/user-preferences.scss';
import '@fortawesome/fontawesome-free/css/all.min.css';
import 'mingcute_icon/font/Mingcute.css';
import '@/ui/theme/nnn_icons/nnn_icons.css';

import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import router from './port/router';
import { KeycloakAuthProvider } from '@/port/provider/KeyCloakAuthProvider';
import { ApiWsClient } from '@/port/provider/ApiWsClient';
import { sentry } from '@/system/operations/sentry';
import { ApiClient } from '@/port/provider/ApiClient';
import { useSession } from '@/domain/repository/Session';
import { useThemeStore } from '@/stores/theme';
import { useUserPreferencesStore } from '@/stores/useUserPreferencesStore';
import { MotionPlugin } from '@vueuse/motion'
import { UAParser } from 'ua-parser-js';
import { MAP_UI_SCALE } from '@/ui/components/map/config/mapConstants';
import { userProfileService } from '@/lib/services/UserProfileService';
// import { initializeCacheSystem } from '@/lib/cache/CacheMigration';
import Logger from '@renanmalato/logger-plus';
import setupRemoteLogger from '@/lib/utils/remoteLogger';

// ------------------------------- //
//  Map UI Scale CSS Var           //
// ------------------------------- //

document.documentElement.style.setProperty('--map-ui-scale', String(MAP_UI_SCALE));

// ------------------------------------------------- //
//                                                   //
//                                                   //
//    Global Browser Error Handlers                  //
//                                                   //
//                                                   //
// ------------------------------------------------- //

// --------------------------------- //
//    Global - CSS Preload Errors    //
// --------------------------------- //

window.addEventListener('error', (event) => {
  if (event.message && event.message.includes('Unable to preload CSS')) {
    console.warn('CSS preload failed, this is non-critical:', event.message)
    event.preventDefault()
    return false
  }
}, true)

// ------------------------------------------- //
//  Global - Unhandled Promise Rejections      //
// ------------------------------------------- //

window.addEventListener('unhandledrejection', (event) => {
  if (event.reason && event.reason.message && event.reason.message.includes('Unable to preload CSS')) {
    console.warn('CSS preload promise rejection, this is non-critical:', event.reason.message)
    event.preventDefault()
    return false
  }
})


// ------------------------------------------------- //
//                                                   //
//                                                   //
//    Global Config & Tooling                        //
//                                                   //
//                                                   //
// ------------------------------------------------- //

// -------------------------------------------- //
//  Suppress DevExtreme License Warning          //
// -------------------------------------------- //

config({ licenseKey: '' });

// ------------------------------------------------- //
//    Setup Remote Logging for Mobile Development    //
// ------------------------------------------------- //

setupRemoteLogger();

// ------------------------------------------- //
//  Cache init causing login redirect loops    //
// ------------------------------------------- //
// Temporary disabled
// initializeCacheSystem().catch(error => {
//   Logger.error('❌ Failed to initialize cache system:', 'Main', { error });
// });


// ------------------------------------------------- //
//                                                   //
//                                                   //
//    App Creation & Plugin Registration             //
//                                                   //
//                                                   //
// ------------------------------------------------- //

const app = createApp(App)
const pinia = createPinia()
app.use(pinia)
app.use(MotionPlugin)
app.use(router)
sentry.init(app)


// ------------------------------------------------- //
//                                                   //
//                                                   //
//    App Configuration                              //
//                                                   //
//                                                   //
// ------------------------------------------------- //

// ------------------------------- //
//    Suppress Vue Warnings        //
// ------------------------------- //

app.config.warnHandler = (msg, vm, trace) => {
  if (msg.includes('Do not use built-in or reserved HTML elements as component id')) { return }
  // Log other warnings normally
  console.warn(msg, trace)
}


// ------------------------------- //
//  Client Side Error Handler      //
// ------------------------------- //

app.config.errorHandler = (err, vm, info) => {
  fetch('/api/crm/client-logs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message: (err as Error).message,
      stack: (err as Error).stack,
      file: info,
      path: window.location.pathname,
      userAgent: navigator.userAgent
    })
  }).catch(e => {
    console.error('⚠️ Failed to send crash report:', e)
  })
}

// -------------------------- //
//  Theme Initialization      //
// -------------------------- //

const themeStore = useThemeStore(pinia)
themeStore.initTheme()


// -------------------------- //
//  User Preferences          //
// -------------------------- //

useUserPreferencesStore(pinia).init()


// -------------------- //
//  Device Detection     //
// -------------------- //

const parser = new UAParser();
const result = parser.getResult();

const device = {
  isMobile: result.device.type === 'mobile',
  isTablet: result.device.type === 'tablet',
  isDesktop: !result.device.type, // If no device type, assume desktop
  isBot: /bot|crawler|spider|crawling/i.test(result.ua),
  deviceType: result.device.type || 'desktop',
  userAgent: navigator.userAgent
};

// Enable this to log the device detection
// Logger.info('📱 Device detection:', 'Main', device);

app.provide('$device', device);


// ------------------------------------------------- //
//                                                   //
//                                                   //
//    Dev-Only Tools                                 //
//                                                   //
//                                                   //
// ------------------------------------------------- //

if (import.meta.env.DEV) {

  /* ~~~~~~~~~~~~~~~~~~~~~~~~~~ */
  /*    Aloware / ngrok         */
  /* ~~~~~~~~~~~~~~~~~~~~~~~~~~ */

  Logger.warn('📞 Aloware calls require ngrok on localhost! Run: ngrok start slingshot-8080', 'Main')

  /* ~~~~~~~~~~~~~~~~~~~~~~~~~~ */
  /*    AnimProfiler            */
  /* ~~~~~~~~~~~~~~~~~~~~~~~~~~ */

  // AnimProfiler: off by default. Enable via console: localStorage.setItem('AP_ENABLED', '1') then reload
  if (localStorage.getItem('AP_ENABLED') === '1') {
    import('@/lib/dev/AnimationProfiler').then(m => m.initAnimationProfiler())
  }

  /* ~~~~~~~~~~~~~~~~~~~~~~~~~~ */
  /*    Token Injection        */
  /* ~~~~~~~~~~~~~~~~~~~~~~~~~~ */

  const params = new URLSearchParams(window.location.search);
  const tokenFromUrl = params.get('token');
  if (tokenFromUrl) {
    Logger.warn('🟡 Injecting token from URL param', 'Main', { devMode: true });
    localStorage.setItem('token', tokenFromUrl);

    // Clean up URL after injection
    const cleanUrl = window.location.origin + window.location.pathname;
    window.history.replaceState({}, document.title, cleanUrl);
  }
}

// Logger.info(`application-version: ${(window as any).applicationVersion}`, 'Main');

// ------------------------------------------------- //
//                                                   //
//                                                   //
//    Authentication & App Mount                     //
//                                                   //
//                                                   //
// ------------------------------------------------- //

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~ */
/*    First-Paint Splash      */
/* ~~~~~~~~~~~~~~~~~~~~~~~~~~ */

const FORCE_SPLASH_MIN = false
const SPLASH_MIN_MS = 5000

function hideAppSplash() {
  const splash = document.getElementById('app-splash')
  if (!splash) { return }

  const wait = Math.max(0, (FORCE_SPLASH_MIN ? SPLASH_MIN_MS : 0) - performance.now())

  setTimeout(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        splash.classList.add('app-splash--hidden')
        splash.addEventListener('transitionend', () => splash.remove(), { once: true })
        setTimeout(() => splash.remove(), 800)
      })
    })
  }, wait)
}

export const ApiWsClientInstance = KeycloakAuthProvider.authenticate().then(async () => {
  const apiUser = await ApiClient.getUser()
  const session = useSession()
  session.setUser(apiUser)

  /* ~~~~~~~~~~~~~~~~~~~~~~~~~~ */
  /*    MongoDB User Record     */
  /* ~~~~~~~~~~~~~~~~~~~~~~~~~~ */

  const preferencesStore = useUserPreferencesStore(pinia)

  try {
    const currentUser = await userProfileService.getCurrentUserFull()
    preferencesStore.hydrateFromServer(currentUser?.preferences)
    session.setVertical(currentUser?.vertical)
  } catch (error) {
    Logger.error('❌ Failed to initialize user MongoDB record', 'Main', { email: apiUser.email, error })
    preferencesStore.markHydrationFailed()
  }

  /* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
  /*    Drop The Login Code From The Address  */
  /* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

  await router.isReady()
  if (/(^#|&)(state|code|session_state|iss)=/.test(router.currentRoute.value.hash)) {
    await router.replace({ ...router.currentRoute.value, hash: '' })
  }

  app.mount('#app')
  hideAppSplash()
}).then(() => {
  return ApiWsClient()
}).catch(async (error) => {

  /* ~~~~~~~~~~~~~~~~~~~~~~~~~~ */
  /*    Handle API Error        */
  /* ~~~~~~~~~~~~~~~~~~~~~~~~~~ */
  
  Logger.error('❌ Failed to initialize API connection', 'Main', { error: error.message, status: error.response?.status })

  const { createApp } = await import('vue')
  const ApiErrorView = (await import('@/ui/views/system/ApiErrorView.vue')).default
  
  const errorApp = createApp(ApiErrorView, { error: error })
  errorApp.mount('#app')
  hideAppSplash()
})

